import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  getSessionRoleName,
  isSessionSuperAdmin,
} from "../../../utils/permissionUtils";
import { organizationHasESourceModule } from "../../../utils/orgModuleAccess";

export { organizationHasESourceModule };

/**
 * Shared e-Source access — same pattern as E-Planner / CAPA:
 * System Administrator / Super Admin → all keys (getAllPermissionKeys).
 * Wait for session load before denying (avoids false redirect to dashboard).
 */
export function useESourceAccess() {
  const { userData, loading } = useUserData();
  const sessionRole = getSessionRoleName(userData);
  const isSystemAdmin =
    isSystemAdministratorRole(sessionRole) || isSessionSuperAdmin(userData);

  const permissions = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );

  /** System Admin / Super Admin: full product access. Others: need org e-Source licence. */
  const moduleAccess =
    isSystemAdmin || organizationHasESourceModule(userData);

  const can = (key) => {
    // Match E-Planner: platform/system admin bypasses org licence + keys.
    if (isSystemAdmin) return true;
    if (!moduleAccess) return false;
    return hasPermission(permissions, key);
  };

  /** Form Management is the only assignable e-Source permission (legacy keys still accepted). */
  const canFormManagement =
    can("ESOURCE_FORM_MANAGEMENT") ||
    can("ESOURCE_DESIGN") ||
    can("ESOURCE_AUDIT_LOG") ||
    can("VIEW_ESOURCE");

  return {
    userData,
    loading,
    isSystemAdmin,
    moduleAccess,
    canView: canFormManagement,
    canDesign: canFormManagement,
    canFormManagement,
    canFill: can("ESOURCE_FILL"),
    canSign: can("ESOURCE_SIGN"),
    canReview: can("ESOURCE_REVIEW"),
    canAmend: can("ESOURCE_AMEND"),
    canAudit: canFormManagement,
  };
}

/** Wait for session before denying access (same idea as ParticipantAccessGate). */
export function ESourceAccessGate({
  allow,
  loading,
  children,
  redirectTo = "/dashboard",
}) {
  if (loading) {
    return <div className="esource-page esource-page--bootstrapping" />;
  }
  if (!allow) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
